import {
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../features/userSlice";
import {
  setBrandMatchLoading,
  setBrandMatchResult,
  setBrandMatchError,
} from "../../../features/leadsManagementSlice";
import { leadsManagementService } from "../../../services/api/leads-management.endpoints";
import store from "../../../store";

async function getLeadsContent(
  oauthTokenHeader: string,
  requestBody: {
    website_shared_uid: string[];
    date_from: string;
    date_to: string;
    page: number;
    page_size: number;
    search?: string;
    report_status?: string;
    brand_uid?: string[];
    qualified_by_brand?: boolean;
    lead_scoring_min?: number;
    has_duplicate_ip?: boolean;
    reported_to_multiple_brands?: boolean;
  },
  setLeadsContent: any,
  trackPromise: any,
) {
  try {
    store.dispatch(setIsLoading(true));
    const response = await leadsManagementService.getLeadsReport(
      oauthTokenHeader,
      requestBody,
    );
    if (!response || response.status !== 200) {
      trackPromise(
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Something went wrong while getting the leads list",
            type: "error",
          }),
        ),
      );
    } else {
      setLeadsContent(response?.data);
    }
  } catch (e: any) {
    trackPromise(
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Unknown error occurred",
          type: "error",
        }),
      ),
    );
  }
}

export interface ReportLeadResult {
  leadId: number;
  succeeded: boolean;
}

/**
 * Reports the given lead ids to their brand partner(s). The endpoint reports
 * each lead individually and can return a mix of successes and failures in
 * the same 200 response (e.g. one brand's site blocked the request while
 * another accepted it), so this returns a per-lead result array rather than
 * a single boolean.
 *
 * Returns null only when the request itself failed (network/HTTP error) —
 * the error snackbar is already shown in that case. Callers should keep the
 * affected leads selected/reportable and let the caller decide what to do
 * next (e.g. leave the confirm dialog open, or let the user retry) rather
 * than assuming the report went through.
 */
async function reportLeadsToBrand(
  oauthTokenHeader: string,
  leadIds: number[],
  trackPromise: any,
): Promise<ReportLeadResult[] | null> {
  try {
    const response = await leadsManagementService.reportLeads(oauthTokenHeader, {
      lead_ids: leadIds,
    });
    if (!response || (response.status !== 200 && response.status !== 204)) {
      trackPromise(
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Something went wrong while reporting leads to brand",
            type: "error",
          }),
        ),
      );
      return null;
    }
    const results: any[] = response?.data?.data ?? [];
    return results.map((result) => ({
      leadId: result.lead_id,
      succeeded: String(result.status).toLowerCase() === "success",
    }));
  } catch (e: any) {
    trackPromise(
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Unknown error occurred while reporting leads to brand",
          type: "error",
        }),
      ),
    );
    return null;
  }
}

export interface RejectLeadsResult {
  updatedIds: number[];
  failedCount: number;
}

// Moves Pending leads to "Rejected". Returns null only on a hard request
// failure (error snackbar already shown); a partial { updated, failed } is
// returned as counts otherwise.
async function rejectLeads(
  oauthTokenHeader: string,
  leadIds: number[],
  trackPromise: any,
): Promise<RejectLeadsResult | null> {
  try {
    const response = await leadsManagementService.rejectLeads(oauthTokenHeader, {
      lead_ids: leadIds,
    });
    if (!response || (response.status !== 200 && response.status !== 204)) {
      trackPromise(
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Something went wrong while rejecting leads",
            type: "error",
          }),
        ),
      );
      return null;
    }
    const updated: any[] = response?.data?.data?.updated ?? [];
    const failed: any[] = response?.data?.data?.failed ?? [];
    return {
      updatedIds: updated.map((item) => item.id),
      failedCount: failed.length,
    };
  } catch (e: any) {
    trackPromise(
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Unknown error occurred while rejecting leads",
          type: "error",
        }),
      ),
    );
    return null;
  }
}

/**
 * Runs the Brand Match diagnostic BQL query for a lead, caching the result by
 * leadId for the current session. Cached results are reused unless
 * forceRefresh is set (the modal's "Re-run query" action).
 *
 * The response shape isn't mapped/typed yet — the endpoint isn't confirmed
 * working, so callers get back whatever the query returns (or null on
 * failure, with the error snackbar already shown).
 */
async function getBrandMatchData(
  oauthTokenHeader: string,
  websiteSharedUid: string,
  leadId: string,
  forceRefresh: boolean,
  trackPromise: any,
): Promise<any | null> {
  const cached = store.getState().leadsManagement.brandMatchCache[leadId];
  if (cached && !forceRefresh) {
    return cached;
  }

  store.dispatch(setBrandMatchLoading({ leadId, loading: true }));
  store.dispatch(setBrandMatchError({ leadId, error: null }));
  try {
    const response = await leadsManagementService.runBrandMatchQuery(
      oauthTokenHeader,
      { websiteSharedUid, leadId },
    );
    if (!response || response.status !== 200) {
      throw new Error("Failed to run brand match query");
    }
    const data = response?.data?.data ?? response?.data;
    store.dispatch(setBrandMatchResult({ leadId, data }));
    return data;
  } catch (e: any) {
    const message =
      e?.parsedText || "Something went wrong while checking brand matches";
    trackPromise(
      store.dispatch(setDisplayErrorSnackBar({ message, type: "error" })),
    );
    store.dispatch(setBrandMatchError({ leadId, error: message }));
    return null;
  } finally {
    store.dispatch(setBrandMatchLoading({ leadId, loading: false }));
  }
}

// reassign_and_report/ is a multistatus endpoint — a 200/204 HTTP response
// does not mean the report succeeded. The real outcome lives under
// response.data.data.status ("success"/"failure"), never the top-level
// `reason` field. On failure, update_failure_reason (if the reassignment
// itself failed) or report_status (if reassignment succeeded but the report
// call failed) explains why.
function describeReassignFailure(result: any): string {
  const code: string | undefined =
    result?.update_status !== "success"
      ? result?.update_failure_reason
      : result?.report_status !== "success"
        ? result?.report_status
        : undefined;
  if (!code) return "Something went wrong while reporting this lead to the brand";

  const labels: Record<string, string> = {
    LEAD_ALREADY_SUCCEEDED: "This lead has already been reported successfully",
    BRAND_MATCH_NOT_FOUND: "Brand match not found",
    not_attempted: "Report to brand was not attempted",
    failed: "Report to brand failed",
  };
  if (labels[code]) return labels[code];

  return String(code)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export interface ReassignAndReportResult {
  succeeded: boolean;
  message: string | null;
}

// The endpoint doesn't always answer with HTTP 200 for a multistatus
// failure — some failure modes come back on a non-2xx status, which
// fetchWrapper treats as a hard error and rejects with `{ text, parsedText,
// status }` (parsedText being only the top-level `reason` field, e.g.
// "Failure" — never the useful part). The full multistatus body is still on
// `text` in that case, so it's parsed the same way as a 200 response rather
// than discarded in favor of the generic reason.
function extractDataPayload(errorLike: any): any | null {
  if (typeof errorLike?.text !== "string") return null;
  try {
    const parsed = JSON.parse(errorLike.text);
    return parsed && typeof parsed.data === "object" ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Manually reassigns a lead to a specific brand and reports it, via
 * POST /partners/leads/reassign_and_report/ ("Report to this brand" in the
 * Brand Match diagnostic modal). Returns null only when no multistatus data
 * payload could be recovered at all (network error, or a body with no
 * `data` object — error snackbar already shown in that case). Any response —
 * 2xx or not — that carries a `data` object is parsed into a result with
 * succeeded: false and a human-readable message so the caller can surface it
 * inline instead of the generic top-level reason.
 */
async function reassignAndReportLead(
  oauthTokenHeader: string,
  leadId: number,
  brandUid: string,
  trackPromise: any,
): Promise<ReassignAndReportResult | null> {
  try {
    const response = await leadsManagementService.reassignAndReportLead(oauthTokenHeader, {
      lead_id: leadId,
      brand_uid: brandUid,
    });
    const result = response?.data?.data ?? {};
    const succeeded = result.status === "success";
    return { succeeded, message: succeeded ? null : describeReassignFailure(result) };
  } catch (e: any) {
    const data = extractDataPayload(e);
    if (data) {
      return { succeeded: false, message: describeReassignFailure(data) };
    }
    trackPromise(
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Unknown error occurred while reporting this lead to the brand",
          type: "error",
        }),
      ),
    );
    return null;
  }
}

export const LeadsManagementEndPointLogic = {
  getLeadsContent,
  reportLeadsToBrand,
  rejectLeads,
  getBrandMatchData,
  reassignAndReportLead,
};
